import React from 'react'
import { Instagram, Facebook as FacebookIcon, Music, Youtube, MessageCircle } from 'lucide-react'

interface ModernSelectPlatformProps {
  selectedPlatform: string
  setSelectedPlatform: React.Dispatch<React.SetStateAction<string>>
}

const platforms = [
  {
    name: 'Instagram',
    icon: Instagram,
    color: 'from-purple-500 to-pink-500',
    description: 'Share photos, videos, and stories',
    enabled: true,
  },
  {
    name: 'Facebook',
    icon: FacebookIcon,
    color: 'from-blue-600 to-blue-400',
    description: 'Connect with friends and family',
    enabled: true,
  },
  {
    name: 'TikTok',
    icon: Music,
    color: 'from-cyan-500 to-pink-500',
    description: 'Create short-form video content',
    enabled: true,
  },
  {
    name: 'Youtube',
    icon: Youtube,
    color: 'from-red-600 to-red-400',
    description: 'Upload and share videos',
    enabled: false,
  },
  {
    name: 'Reddit',
    icon: MessageCircle,
    color: 'from-orange-600 to-orange-400',
    description: 'Join communities and discussions',
    enabled: false,
  },
]

export default function ModernSelectPlatform({
  selectedPlatform,
  setSelectedPlatform,
}: ModernSelectPlatformProps): JSX.Element {
  return (
    <div>
      <h2
        style={{
          fontSize: '32px',
          fontWeight: 700,
          background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '12px',
        }}
      >
        Choose a Platform
      </h2>
      <p
        style={{
          fontSize: '16px',
          color: 'rgba(255, 255, 255, 0.6)',
          marginBottom: '32px',
        }}
      >
        Select the social media platform where you want to publish your content
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px',
        }}
      >
        {platforms.map((platform) => {
          const PlatformIcon = platform.icon
          const isSelected = selectedPlatform === platform.name
          const isDisabled = !platform.enabled

          return (
            <div
              key={platform.name}
              onClick={() => {
                if (!isDisabled) {
                  setSelectedPlatform(platform.name)
                }
              }}
              style={{
                padding: '24px',
                borderRadius: '16px',
                background: isSelected
                  ? 'rgba(168, 85, 247, 0.15)'
                  : 'rgba(30, 41, 59, 0.4)',
                border: `2px solid ${
                  isSelected ? '#a855f7' : 'rgba(168, 85, 247, 0.2)'
                }`,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: isDisabled ? 0.5 : 1,
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                if (!isDisabled && !isSelected) {
                  e.currentTarget.style.background = 'rgba(168, 85, 247, 0.08)'
                  e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.4)'
                  e.currentTarget.style.transform = 'translateY(-4px)'
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(168, 85, 247, 0.3)'
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.background = 'rgba(30, 41, 59, 0.4)'
                  e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.2)'
                }
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = 'none'
              }}
            >
              {/* Selection Indicator */}
              {isSelected && (
                <div
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}

              {/* Platform Icon */}
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '12px',
                  background: `linear-gradient(135deg, ${platform.color.split(' ')[1]} 0%, ${platform.color.split(' ')[3]} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px',
                }}
              >
                <PlatformIcon size={28} color="white" />
              </div>

              {/* Platform Name */}
              <h3
                style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: 'rgba(255, 255, 255, 0.9)',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {platform.name}
                {!platform.enabled && (
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: 'rgba(255, 255, 255, 0.5)',
                      padding: '2px 8px',
                      borderRadius: '6px',
                      background: 'rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    Coming Soon
                  </span>
                )}
              </h3>

              {/* Platform Description */}
              <p
                style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.6)',
                  lineHeight: '1.5',
                }}
              >
                {platform.description}
              </p>
            </div>
          )
        })}
      </div>

      {/* Helper Text */}
      <div
        style={{
          marginTop: '32px',
          padding: '16px 20px',
          borderRadius: '12px',
          background: 'rgba(168, 85, 247, 0.1)',
          border: '1px solid rgba(168, 85, 247, 0.2)',
        }}
      >
        <p
          style={{
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.7)',
            margin: 0,
          }}
        >
          💡 <strong>Tip:</strong> Each platform has different content requirements. We&apos;ll
          guide you through the specific requirements in the next step.
        </p>
      </div>
    </div>
  )
}